import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";

const emptyForm = {
  branchId: "",
  name: "",
  code: "",
  type: "STORE",
  phone: "",
  email: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  postalCode: "",
  country: "IN",
  isActive: true,
};

export default function StoreModal({
  open,
  onClose,
  onSave,
  initialData = null,
  branches = [],
}) {
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!open) return;

    setForm(
      initialData
        ? {
            branchId: initialData.branchId || "",
            name: initialData.name || "",
            code: initialData.code || "",
            type: initialData.type || "STORE",
            phone: initialData.phone || "",
            email: initialData.email || "",
            addressLine1: initialData.addressLine1 || "",
            addressLine2: initialData.addressLine2 || "",
            city: initialData.city || "",
            state: initialData.state || "",
            postalCode: initialData.postalCode || "",
            country: initialData.country || "IN",
            isActive: initialData.isActive ?? true,
          }
        : emptyForm,
    );
  }, [open, initialData]);

  if (!open) return null;

  const update = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));

  const handleSubmit = (event) => {
    event.preventDefault();

    onSave({
      ...form,
      branchId: form.branchId || undefined,
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      phone: form.phone.trim() || undefined,
      email: form.email.trim() || undefined,
      addressLine1: form.addressLine1.trim() || undefined,
      addressLine2: form.addressLine2.trim() || undefined,
      city: form.city.trim() || undefined,
      state: form.state.trim() || undefined,
      postalCode: form.postalCode.trim() || undefined,
      country: form.country.trim().toUpperCase() || "IN",
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-8 py-6">
          <div>
            <h2 className="text-2xl font-bold">
              {initialData ? "Edit Store" : "Add Store"}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Manage stores and warehouses by branch.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl bg-gray-100 p-3">
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid gap-5 p-8 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium">Branch</label>
              <select value={form.branchId} onChange={(e) => update("branchId", e.target.value)} className="h-12 w-full rounded-xl border px-4">
                <option value="">No Branch / Central Warehouse</option>
                {branches.filter((branch) => branch.isActive).map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name} ({branch.code})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Location Type *</label>
              <select value={form.type} onChange={(e) => update("type", e.target.value)} className="h-12 w-full rounded-xl border px-4">
                <option value="STORE">Retail Store</option>
                <option value="WAREHOUSE">Warehouse</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Store Name *</label>
              <input required value={form.name} onChange={(e) => update("name", e.target.value)} className="h-12 w-full rounded-xl border px-4" />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium">Store Code *</label>
              <input required value={form.code} onChange={(e) => update("code", e.target.value.toUpperCase())} className="h-12 w-full rounded-xl border px-4" />
            </div>

            <input value={form.phone} placeholder="Phone Number" onChange={(e) => update("phone", e.target.value)} className="h-12 rounded-xl border px-4" />
            <input type="email" value={form.email} placeholder="Email Address" onChange={(e) => update("email", e.target.value)} className="h-12 rounded-xl border px-4" />

            <div className="md:col-span-2">
              <input value={form.addressLine1} placeholder="Address Line 1" onChange={(e) => update("addressLine1", e.target.value)} className="h-12 w-full rounded-xl border px-4" />
            </div>

            <div className="md:col-span-2">
              <input value={form.addressLine2} placeholder="Address Line 2" onChange={(e) => update("addressLine2", e.target.value)} className="h-12 w-full rounded-xl border px-4" />
            </div>

            <input value={form.city} placeholder="City" onChange={(e) => update("city", e.target.value)} className="h-12 rounded-xl border px-4" />
            <input value={form.state} placeholder="State" onChange={(e) => update("state", e.target.value)} className="h-12 rounded-xl border px-4" />
            <input value={form.postalCode} placeholder="Postal Code" onChange={(e) => update("postalCode", e.target.value)} className="h-12 rounded-xl border px-4" />

            <select value={form.isActive ? "active" : "inactive"} onChange={(e) => update("isActive", e.target.value === "active")} className="h-12 rounded-xl border px-4">
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div className="sticky bottom-0 flex justify-end gap-3 border-t bg-white px-8 py-5">
            <button type="button" onClick={onClose} className="h-11 rounded-xl border px-6">Cancel</button>
            <button type="submit" className="h-11 rounded-xl bg-primary-800 px-6 text-white">
              {initialData ? "Save Changes" : "Create Store"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}